import React from "react";
import "./Navbar.css";

/**
 * RAVEN Navbar (Metrea v2 look with v1 shadow & slim ribbons)
 *
 * Usage (either style works):
 *   <Navbar
 *     start={<Logo appName="Raven" />}
 *     center={<Tabs />}
 *     end={<UserMenu />}
 *   />
 *
 *   <Navbar>
 *     <NavbarStart><Logo /></NavbarStart>
 *     <NavbarCenter><Tabs /></NavbarCenter>
 *     <NavbarEnd><UserMenu /></NavbarEnd>
 *   </Navbar>
 */

function pickSlot(childrenArr, slotName) {
	// Find wrapper by displayName/_slot and extract its inner children
	const found = childrenArr.find(
		(c) =>
			c?.type?._slot === slotName ||
			c?.type?.displayName === `Navbar${slotName[0].toUpperCase()}${slotName.slice(1)}`
	);
	return found ? found.props.children : null;
}

export default function Navbar({
	start,
	center,
	end,
	appName = "Raven",
	onLogoClick,
	className = "",
	children,
}) {
	const kids = React.Children.toArray(children || []);
	const slotStart = start ?? pickSlot(kids, "start") ?? <Logo appName={appName} onClick={onLogoClick} />;
	const slotCenter = center ?? pickSlot(kids, "center");
	const slotEnd = end ?? pickSlot(kids, "end");

	return (
		<div data-theme="light" className={`headerNavbar ${className}`}>
			<div className="navbar-bar">
				{/* left ribbon */}
				<div className="ribbon slim-ribbon" aria-hidden="true" />

				<div className="navbar-inner">
					<div className="navbar-row">
						<div className="navbar-start">{slotStart}</div>
						<div className="navbar-center">{slotCenter}</div>
						<div className="navbar-end">{slotEnd}</div>
					</div>
				</div>

				{/* right ribbon */}
				<div className="ribbon slim-ribbon" aria-hidden="true" />
			</div>
		</div>
	);
}

/* ---------- Subcomponents / named exports ---------- */

export function NavbarStart({ children }) {
	return <>{children}</>;
}
NavbarStart._slot = "start";
NavbarStart.displayName = "NavbarStart";

export function NavbarCenter({ children }) {
	return <>{children}</>;
}
NavbarCenter._slot = "center";
NavbarCenter.displayName = "NavbarCenter";

export function NavbarEnd({ children }) {
	return <>{children}</>;
}
NavbarEnd._slot = "end";
NavbarEnd.displayName = "NavbarEnd";

/** Optional: brand/title text if you want to import it directly */
export function NavbarTitle({ children, appName = "Raven" }) {
	return <span className="brand-title">{children ?? appName}</span>;
}
NavbarTitle.displayName = "NavbarTitle";

export function Logo({ appName = "Raven", onClick }) {
	return (
		<div className="info" onClick={onClick} role={onClick ? "button" : undefined}>
			<div className="logo">
				<svg
					width="18"
					height="24"
					viewBox="0 0 18 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<g clipPath="url(#clip0_2974_273)">
						<path
							d="M8.79513 13.424L9.47244 13.7367C9.96848 13.9657 10.3478 14.378 10.5253 14.8721L3.91488 22.7421L3.44492 24L12.309 19.4101L14.7432 16.935C15.3578 16.3096 15.5776 15.4133 15.3162 14.5953L14.9853 13.5606L18 12L14.9853 10.4394L15.3162 9.40472C15.5776 8.58674 15.3584 7.68985 14.7432 7.06502L12.309 4.58988L3.44492 0L3.91488 1.25789L10.5253 9.12794C10.3478 9.62202 9.96848 10.0343 9.47244 10.2633L8.79513 10.576L0 9.3635L5.71091 12L0 14.6365L8.79513 13.424ZM10.6911 15.3785L11.2107 17.0045L4.64807 22.5737L10.6911 15.3785ZM5.51162 22.43L11.3591 17.4679L11.8893 19.1274L5.51162 22.43ZM14.4048 16.6358L12.2705 18.8065L10.9922 14.8079C10.788 14.1689 10.3074 13.6348 9.67421 13.3421L9.60902 13.3121L12.8925 12.8592L14.5166 13.6089L14.8723 14.7225C15.0871 15.3915 14.9077 16.1246 14.4048 16.6358ZM14.7035 13.2067L13.7077 12.7467L16.2747 12.3928L14.7035 13.2067ZM16.2747 11.6072L13.7077 11.2533L14.7035 10.7933L16.2747 11.6072ZM11.8893 4.87255L11.3591 6.53207L5.51162 1.57001L11.8893 4.87255ZM4.64807 1.42631L11.2114 6.99553L10.6917 8.62148L4.64807 1.42631ZM9.67483 10.6579C10.3081 10.3658 10.7886 9.83167 10.9922 9.19213L12.2705 5.1935L14.4048 7.36419C14.9077 7.87535 15.0871 8.60853 14.8729 9.27752L14.5172 10.3911L12.8932 11.1408L9.60902 10.6879L9.67483 10.6579ZM2.87811 10.2039L8.05511 10.9176L6.23922 11.7556L2.87811 10.2039ZM8.86963 11.0301L15.9078 12L2.87749 13.7961L8.86963 11.0301Z"
							fill="#2C3C4C"
						/>
					</g>
					<defs>
						<clipPath id="clip0_2974_273">
							<rect width="18" height="24" fill="white" />
						</clipPath>
					</defs>
				</svg>
			</div>

			<div className="logo-separator" aria-hidden="true" />

			<div className="brand">
				<span className="brand-title">{appName}</span>
			</div>
		</div>
	);
}

/* Also attach as static props if you prefer <Navbar.Start> style */
Navbar.Start = NavbarStart;
Navbar.Center = NavbarCenter;
Navbar.End = NavbarEnd;
Navbar.Logo = Logo;
Navbar.Title = NavbarTitle;
